import { fn } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismInput,
  PrismMessage,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import type CustomTagDefinition from 'lumen/models/custom-tag-definition';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface Signature {
  Args: {
    definition: CustomTagDefinition;
    onClose: () => void;
    onSuccess: () => void;
  };
}

export default class SettingsCustomPropertyTagsModalsModalDeleteDefinition extends Component<Signature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked confirmValue = '';

  deleteTask = task({ drop: true }, async (): Promise<void> => {
    try {
      await this.args.definition.destroyRecord();
      this.toast.success(
        this.intl.t(
          'settings.property_tags.custom_tags.form.toast.tag_deleted',
        ),
        { showIcon: true },
      );
      this.args.onSuccess();
    } catch (error) {
      this.errorTracking.captureException(error, {
        feature: 'custom-property-tags.delete-definition',
      });
      void this.alerting.showErrorAlert();
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  get isDeleteDisabled(): boolean {
    return (
      this.confirmValue.trim().toLowerCase() !==
      this.args.definition.name.toLowerCase()
    );
  }

  <template>
    <PrismModal
      @onClose={{@onClose}}
      class="modal--delete-confirmation"
      data-test-modal="delete-definition"
    >
      <:default>
        <div class="modal--padded flex flex-col gap-400">
          <div class="flex justify-center">
            <PrismMessage
              @description={{t
                "settings.property_tags.custom_tags.form.delete_description"
              }}
              @illustration="100x100_warning"
              @title={{t "general.delete_with_text" text=@definition.name}}
              data-test-id="delete-confirm-message"
            />
          </div>
          <PrismInput
            @autofocus={{true}}
            @label={{t "general.type_to_confirm" text=@definition.name}}
            @onInput={{pick "target.value" (fn (mut this.confirmValue))}}
            @required={{true}}
            @requiredErrorMessage={{t
              "settings.property_tags.custom_tags.form.validations.delete_confirm_name"
            }}
            @value={{this.confirmValue}}
            data-test-input="delete-confirm"
          />
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-200 px-500">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.deleteTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
            data-test-button="cancel"
          />
          <PrismButton
            @buttonType="critical"
            @isDisabled={{or this.isDeleteDisabled this.deleteTask.isRunning}}
            @isLoading={{this.deleteTask.isRunning}}
            @label={{t "general.delete_forever"}}
            @onClick={{this.deleteTask.perform}}
            data-test-button="delete"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
